import DOMPurify from 'dompurify';

export interface MarkdownToHtmlOptions {
  /**
   * If provided, every <img> in the rendered output is wrapped in an
   * anchor pointing at this URL (target=_blank). GitHub Issues inline
   * images use SameSite=Lax cookies, so they cannot load cross-origin
   * inside EM3. Wrapping with a link to the issue page lets the user
   * click through to GitHub where the screenshot loads via their web
   * session.
   */
  wrapImagesWith?: string;
}

/**
 * Lightweight markdown → sanitized HTML converter for issue bodies in
 * the Support Tool. Not a full markdown engine — handles the small
 * subset GitHub Issues actually emit for tickets created here, plus
 * inline images that GitHub's web UI inserts when users paste/drop a
 * screenshot into an issue body.
 */
export function markdownToHtml(markdown: string, options: MarkdownToHtmlOptions = {}): string {
  const rawHtml = markdown
    .replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1">')
    .replace(/### (.*?)\n/g, '<h3>$1</h3>')
    .replace(/## (.*?)\n/g, '<h2>$1</h2>')
    .replace(/# (.*?)\n/g, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>')
    .replace(/\n/g, '<br>');

  let sanitized = DOMPurify.sanitize(rawHtml, {
    ADD_TAGS: ['img'],
    ADD_ATTR: ['src', 'alt', 'target', 'rel'],
  });

  if (options.wrapImagesWith) {
    const escapedHref = options.wrapImagesWith
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    sanitized = sanitized.replace(
      /<img[^>]*>/g,
      (img) => `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer">${img}</a>`,
    );
  }

  return sanitized;
}
